import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CircularProgress, Typography } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth, useCompany } from './hooks/useAuth';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { HomePage } from './pages/HomePage';
import { PostPage } from './pages/PostPage';
import { SettingsPage } from './pages/SettingsPage';
import { HelpPage } from './pages/HelpPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { AboutAppPage } from './pages/AboutAppPage';
import { AdminPage } from './pages/AdminPage';
import { ThemePostsPage } from './pages/ThemePostsPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { ThemeManagementPage } from './pages/ThemeManagementPage';
import { CompanyManagementPage } from './pages/CompanyManagementPage';
import { AccessLinkPage } from './pages/AccessLinkPage';
import { SuspendedPage } from './pages/SuspendedPage';
import CancellationPage from './pages/CancellationPage.tsx';
import { AuditLogPage } from './pages/AuditLogPage';
import { CommercialLawPage } from './pages/CommercialLawPage';
import { PricingPage } from './pages/PricingPage';
import { CompanyRegistrationPage } from './pages/CompanyRegistrationPage';
import { VoiceDetailPage } from './pages/VoiceDetailPage';
import { QrLoginPage } from './pages/QrLoginPage';
import { RegistrationCompletePage } from './pages/RegistrationCompletePage';
import { ScanRequiredPage } from './pages/ScanRequiredPage';
import { InitialRoute } from './components/InitialRoute';
import { AppColors } from './theme/colors';
import { AppTextStyles } from './theme/typography';
import { buildFunctionUrl } from './utils/functionsUrl';

const theme = createTheme({
  palette: {
    primary: {
      main: AppColors.primary,
      dark: AppColors.primaryDark,
      light: AppColors.primaryLight,
    },
    secondary: {
      main: AppColors.secondary,
    },
    error: {
      main: AppColors.error,
    },
    success: {
      main: AppColors.success,
    },
    warning: {
      main: AppColors.warning,
    },
    info: {
      main: AppColors.info,
    },
    background: {
      default: AppColors.background,
      paper: AppColors.surface,
    },
    text: {
      primary: AppColors.textPrimary,
      secondary: AppColors.textSecondary,
    },
  },
  typography: {
    fontFamily: '"Noto Sans JP", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: AppTextStyles.displayLarge,
    h2: AppTextStyles.displayMedium,
    h3: AppTextStyles.displaySmall,
    h4: AppTextStyles.headlineLarge,
    h5: AppTextStyles.headlineMedium,
    h6: AppTextStyles.headlineSmall,
    subtitle1: AppTextStyles.titleLarge,
    subtitle2: AppTextStyles.titleMedium,
    body1: AppTextStyles.bodyLarge,
    body2: AppTextStyles.bodyMedium,
    button: AppTextStyles.buttonMedium,
    caption: AppTextStyles.labelLarge,
    overline: AppTextStyles.labelSmall,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${AppColors.primary} 0%, ${AppColors.primaryLight} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${AppColors.primaryDark} 0%, ${AppColors.primary} 100%)`,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: AppColors.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: AppColors.primary,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': {
              color: AppColors.primary,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: AppColors.primary,
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const location = useLocation();
  const navigate = useNavigate();

  // 退会処理が完了した場合、自動的にログアウトしてログイン画面にリダイレクト
  useEffect(() => {
    if (!loading && !companyLoading && user && company) {
      const cancellationStatus = (company as any).cancellationStatus;
      if (cancellationStatus === 'completed') {
        // メッセージを表示してから3秒後にリダイレクト
        const timer = setTimeout(() => {
          const userType = (user as any)?.userType || '';
          const isAdmin = ['admin', 'superadmin'].includes(userType);
          
          const landing = isAdmin ? '/admin-login' : '/scan-required';

          if (isAdmin) {
            // 管理者ログイン情報をクリア
            localStorage.removeItem('adminUserId');
            // Cookieからも管理者ログイン情報をクリア
            fetch(buildFunctionUrl('clearAdminLoginRemember'), {
              method: 'POST',
              credentials: 'include',
            }).catch(() => {});
          }

          // 退会した会社に入ったままにしないよう、権限によらずサインアウトする。
          // 残っているとログイン中と判定され、退会した会社のホームへ戻れてしまう。
          import('firebase/auth').then(({ getAuth, signOut }) => {
            const auth = getAuth();
            if (auth.currentUser) {
              signOut(auth).finally(() => {
                navigate(landing, { replace: true });
              });
            } else {
              navigate(landing, { replace: true });
            }
          }).catch(() => {
            navigate(landing, { replace: true });
          });
        }, 3000); // 3秒待つ
        
        // クリーンアップ関数でタイマーをクリア
        return () => clearTimeout(timer);
      }
    }
  }, [user, company, loading, companyLoading, navigate]);

  // 切り替え中はリダイレクトをスキップ
  const isSwitching = sessionStorage.getItem('switching_user') === 'true';
  
  if (isSwitching) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: AppColors.surface,
        position: 'relative',
      }}>
        <CircularProgress 
          size={60}
          thickness={4}
          sx={{ color: AppColors.primary }}
        />
        
        {/* スナックバー風のメッセージ（Flutter版と同じスタイル） */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: '#424242',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 1,
            boxShadow: '0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)',
            width: 300,
            maxWidth: '90%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ fontSize: 14, color: 'white', lineHeight: 1.43 }}>
            一般ユーザーに切り替え中...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (loading || companyLoading) {
    return <div>読み込み中...</div>;
  }

  if (!user) {
    // QRから入った利用者は会社IDもパスワードも知らないため、QRの読み取りを促す画面へ送る。
    // URLの切り替えで強制ログアウトされた場合もここを通る。
    // それ以外（管理者）は管理者ログイン画面へ送る。
    const cameFromQr =
      typeof window !== 'undefined' && window.localStorage.getItem('loginMethod') === 'qr';
    return <Navigate to={cameFromQr ? '/scan-required' : '/admin-login'} replace />;
  }

  // 解約完了済みの場合はログアウト処理中（useEffectで処理される）
  if (company && (company as any).cancellationStatus === 'completed') {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        p: 3,
      }}>
        <Typography sx={{ fontSize: 16, color: AppColors.textSecondary }}>
          退会処理が完了しました。ログイン画面に移動しています...
        </Typography>
      </Box>
    );
  }

  // 停止中の遷移制御
  if (company && (company as any).serviceStatus === 'suspended') {
    const userType = (user as any)?.userType || '';
    const isAdmin = ['admin', 'superadmin'].includes(userType);
    if (isAdmin) {
      if (location.pathname !== '/admin/company-management') {
        return <Navigate to="/admin/company-management" replace />;
      }
    } else {
      if (location.pathname !== '/suspended') {
        return <Navigate to="/suspended" replace />;
      }
    }
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  // ローディング中は待機
  if (loading) {
    return <div>読み込み中...</div>;
  }

  // ユーザーがログインしていない場合は管理者ログイン画面へ
  if (!user) {
    return <Navigate to="/admin-login" replace />;
  }

  // 管理者権限のチェック（userTypeで判定）
  if (!['admin', 'superadmin'].includes(user.userType || '')) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ width: '100%', minHeight: '100vh', margin: 0, padding: 0 }}>
        <Router>
          <Routes>
          {/* 旧ログイン画面。ブックマークから来た人を入口へ送る */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          {/* 有料のプラン選択は廃止した。無料登録に一本化したため、
              古いリンクやブックマークから来た場合は会社登録画面へ送る。 */}
          <Route path="/plan-selection" element={<Navigate to="/company-register" replace />} />
          {/* 専用URL / QRコードからのログイン（合言葉付き） */}
          <Route path="/q/:token" element={<QrLoginPage />} />
          {/* QRで入った利用者がログアウトしたときの着地点 */}
          <Route path="/scan-required" element={<ScanRequiredPage />} />
          <Route path="/admin-login" element={<AdminLoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/company-register" element={<CompanyRegistrationPage />} />
          {/* 決済完了後の着地点 */}
          <Route path="/registration-complete" element={<RegistrationCompletePage />} />
          <Route path="/commercial-law" element={<CommercialLawPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suspended"
            element={
              <ProtectedRoute>
                <SuspendedPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/post/new"
            element={
              <ProtectedRoute>
                <PostPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/posts"
            element={
              <ProtectedRoute>
                <PostPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <HelpPage />
              </ProtectedRoute>
            }
          />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route
            path="/about-app"
            element={
              <ProtectedRoute>
                <AboutAppPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/voice/:postId"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <VoiceDetailPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/theme-posts"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <ThemePostsPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/user-management"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <UserManagementPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/theme-management"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <ThemeManagementPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/company-management"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <CompanyManagementPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/access-link"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AccessLinkPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-log"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AuditLogPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cancellation"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <CancellationPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<InitialRoute />} />
          </Routes>
        </Router>
      </Box>
    </ThemeProvider>
  );
};

export default App;